import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import MoreMenu, { swatch } from './MoreMenu';
import { LabelPicker } from './TodoEditor';
import { ReminderPicker, cleanReminders, reminderLabel } from './RepeatReminders';
import { Icon } from './icons';
import { ImageViewer, NoteImage, NoteVoice } from './NoteMedia';
import { ArchiveIcon, ChecklistIcon, ImagePick, Palette, PinIcon } from './TodoNotes';
import useReorder from './useReorder';
import { MicButton, VoiceBar, canRecord, useVoiceRecorder } from '../VoiceNote';
import { fileSize } from '../ChatFile';
import { MAX_LINES, audioOf, editedLabel, imagesOf, lineId, splitLines } from './notes';

const SAVE_AFTER = 700;   // ms of quiet typing before the title and body are saved

/**
 * ONE NOTE, OPEN (batch 5): Keep's editor, as a dialog in the middle of the
 * screen and a sheet along the foot of it on a phone, painted the note's
 * colour.
 *
 * WHAT IS TYPED SAVES ITSELF. The title and the body are sent a moment after
 * the typing stops (and at once when the editor closes), with a quiet
 * "Saving…" and then "Saved" in the corner, so there is no Save button to
 * forget.
 *
 * A CHECKLIST is edited a line at a time, each change its own small call -
 * tick, retype, add, remove - because in a shared list two people may be
 * ticking the same shopping list at once, and sending the whole list back
 * would undo the other person's ticks. Only a reorder sends the list whole.
 * Enter on a line starts the next one under it; Backspace on an empty line
 * takes it away. Ticked lines fold away at the bottom under "N ticked
 * items". "Show checkboxes" turns the body's lines into a checklist and
 * "Hide checkboxes" turns it back.
 *
 * PHOTOS come from the button, from a paste, or dropped on the editor; VOICE
 * NOTES from chat's own recorder (VoiceNote.js). Both go up as attachments
 * of the note and are fetched with the sign-in to be shown (NoteMedia.js).
 *
 * TodoApp does every call: `on` is its set of handlers, each returning a
 * promise, and `item` is always the note as the page now has it.
 */
export default function NoteEditor({ item, on, lists, labels, load, download, busy = false,
                                     fixedList = false, now = new Date(), onClose, canRemove = () => true }) {
  const [title, setTitle] = useState(item.title || '');
  const [body, setBody] = useState(item.notes || '');
  const [state, setState] = useState('idle');     // idle | waiting | saving | saved | failed
  const [err, setErr] = useState(null);
  const [showTicked, setShowTicked] = useState(false);
  const [edits, setEdits] = useState({});          // line id -> text being typed
  const [draft, setDraft] = useState({ at: null, text: '' });   // a new line, before `at` (null: at the end)
  const [viewing, setViewing] = useState(null);
  const [dropping, setDropping] = useState(false);
  const [meta, setMeta] = useState(null);          // 'labels' | 'reminders' while open
  // "Show checkboxes" on a note with no words: the server keeps no empty
  // checklist (it answers null), so the list shows here, waiting for its
  // first line, and only that line makes it a checklist on the server.
  const [wantList, setWantList] = useState(false);
  const timer = useRef(null);
  const sent = useRef({ title: item.title || '', notes: item.notes || '' });
  const lineRefs = useRef({});
  const draftRef = useRef(null);
  const focusNext = useRef(null);
  const bodyRef = useRef(null);

  const hasList = Array.isArray(item.checklist) && item.checklist.length > 0;
  const listMode = hasList || wantList;

  // The body grows with what is written, as Keep's does - no grip in the
  // corner to drag. The dialog scrolls when the note is longer than it.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body, listMode]);
  const hasFiles = (item.attachments || []).length > 0;
  const { open: openLines, ticked } = splitLines(item.checklist);
  const imgs = imagesOf(item);
  const voices = audioOf(item);
  const files = (item.attachments || []).filter((a) => !imgs.includes(a) && !voices.includes(a));
  const name = item.title ? `"${item.title}"` : 'this note';
  const home = lists.find((l) => l.id === item.list_id) || null;
  const tags = (item.labels || []).map((id) => labels.find((b) => b.id === id)).filter(Boolean);
  const rems = Array.isArray(item.reminders) ? item.reminders : [];
  const fail = (e, words) => setErr(e?.response?.data?.error || words);

  /* ------------------------------------------------------- the autosave */

  const save = async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const want = { title: title.trim(), notes: body };
    const patch = {};
    if (want.title !== sent.current.title) patch.title = want.title;
    if (!hasList && (want.notes || '') !== (sent.current.notes || '')) patch.notes = want.notes.trim() ? want.notes : null;
    if (!Object.keys(patch).length) { setState((s) => (s === 'waiting' ? 'saved' : s)); return; }
    // A note needs a title, a body, a checklist or a file; the server
    // refuses one emptied of all of them, so that is not sent - closing it
    // lets it go instead, as Keep does.
    if (!want.title && !body.trim() && !hasList && !hasFiles) { setState('empty'); return; }
    sent.current = { title: want.title, notes: hasList ? sent.current.notes : want.notes };
    setState('saving');
    try {
      await on.patch(item, patch);
      setState('saved');
    } catch (e) {
      setState('failed');
      fail(e, 'That did not save');
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  const typed = (fn) => (e) => {
    fn(e.target.value);
    setState('waiting');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => saveRef.current(), SAVE_AFTER);
  };

  const close = async () => {
    if (!title.trim() && !body.trim() && !hasList && !hasFiles) {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      on.remove(item);   // hidden, with an Undo, like any removal
      return;
    }
    await saveRef.current();
    onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  // Escape closes (and saves) - but a palette, a menu or a photo open over
  // the editor takes its own Escape first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.tdn-editor .tda-menu, .tdn-editor .tdn-swatches, .tdn-viewer')) return;
      e.stopPropagation();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); saveRef.current(); } }, []);

  /* --------------------------------------------------------- the lines */

  useEffect(() => {
    const f = focusNext.current;
    if (!f) return;
    focusNext.current = null;
    const el = f === 'draft' ? draftRef.current : lineRefs.current[f];
    if (el) el.focus();
  });

  /** Where a line drawn at `at` among the unticked ones sits in the whole list. */
  const fullIndex = (at) => {
    const all = item.checklist || [];
    const before = openLines[at];
    return before ? all.findIndex((l) => l.id === before.id) : null;
  };

  const addDraft = async () => {
    const text = draft.text.trim();
    if (!text) return;
    if ((item.checklist || []).length >= MAX_LINES) { setErr(`A checklist can have ${MAX_LINES} lines at most`); return; }
    const at = draft.at;
    const index = at === null ? null : fullIndex(at);
    setDraft({ at: at === null ? null : at + 1, text: '' });
    focusNext.current = 'draft';
    if (!hasList) {
      // The first line of a new checklist makes it one.
      try {
        await on.patch(item, { checklist: [{ id: lineId(), text, done: false }], notes: null });
        setWantList(false);
        sent.current = { ...sent.current, notes: '' };
      } catch (e) { fail(e, 'That line did not go in'); }
      return;
    }
    try { await on.addLine(item, text, index); } catch (e) { fail(e, 'That line did not go in'); }
  };

  const lineText = (l) => (edits[l.id] !== undefined ? edits[l.id] : l.text);
  const keepText = async (l) => {
    const text = edits[l.id];
    if (text === undefined) return;
    setEdits((prev) => { const n = { ...prev }; delete n[l.id]; return n; });
    if (!text.trim() || text === l.text) return;
    try { await on.lineText(item, l, text.trim()); } catch (e) { fail(e, 'That line did not save'); }
  };
  const dropLine = async (l, focusId) => {
    if (focusId) focusNext.current = focusId;
    try { await on.delLine(item, l); } catch (e) { fail(e, 'That line could not be removed'); }
  };
  const tick = async (l, done) => {
    try { await on.tick(item, l, done); } catch (e) { fail(e, 'That did not tick'); }
  };

  const reorder = useReorder((group, from, to) => {
    if (group !== 'lines') return;
    const next = openLines.filter((_, i) => i !== from);
    next.splice(to, 0, openLines[from]);
    on.reorder(item, [...next, ...ticked]).catch((e) => fail(e, 'That did not move'));
  });

  const toggleChecklist = async () => {
    if (wantList) { setWantList(false); return; }
    if (!hasList && !body.trim()) {
      setWantList(true);
      focusNext.current = 'draft';
      return;
    }
    await saveRef.current();
    // The words the editor will show, set before the call, so the body is
    // never drawn for a moment with what it held before the checklist.
    const before = body;
    const text = hasList ? (item.checklist || []).map((l) => l.text).filter((s) => String(s).trim()).join('\n') : '';
    setBody(text);
    sent.current = { ...sent.current, notes: text };
    try {
      await on.toggleChecklist(item, !hasList, before);
    } catch (e) {
      setBody(before);
      sent.current = { ...sent.current, notes: before };
      fail(e, 'That did not change');
    }
  };

  /* ---------------------------------------------------- photos and voice */

  const upload = async (list, durationMs = null) => {
    if (!list.length) return;
    setErr(null);
    try { await on.upload(item, list, durationMs); } catch (e) { fail(e, 'That could not be added'); }
  };
  const recorder = useVoiceRecorder({
    onRecorded: (file, ms) => upload([file], ms),
    onError: (words) => setErr(words),
    resetKey: item.id
  });
  const pickImages = (dt) => [...(dt?.files || [])].filter((f) => /^image\//.test(f.type));

  const editedAt = editedLabel(item.updated_at || item.created_at, now);
  const status = state === 'waiting' || state === 'saving' ? 'Saving…'
    : state === 'saved' ? 'Saved' : state === 'failed' ? 'Not saved'
      : state === 'empty' ? 'Empty - closing it deletes it' : '';

  const line = (l, i, isTicked) => (
    <li key={l.id} className={`tdn-line${isTicked ? ' is-done' : ''}${isTicked ? '' : reorder.markOf('lines', i, openLines.length)}`}
        data-line={l.id} {...(isTicked ? {} : { 'data-reorder-item': '' })}>
      {!isTicked ? (
        <span className="tdn-grip" aria-hidden="true" title="Drag to reorder"
              onPointerDown={(e) => reorder.start(e, 'lines', i)}><Icon name="grip" /></span>
      ) : <span className="tdn-grip is-gap" aria-hidden="true" />}
      <input type="checkbox" checked={isTicked} disabled={busy}
             aria-label={isTicked ? `Untick "${l.text}"` : `Tick "${l.text}"`}
             onChange={(e) => tick(l, e.target.checked)} />
      <input type="text" className="tdn-line-text" value={lineText(l)} maxLength={500}
             aria-label={`Line: ${l.text}`} ref={(el) => { lineRefs.current[l.id] = el; }}
             onChange={(e) => { const v = e.target.value; setEdits((prev) => ({ ...prev, [l.id]: v })); }}
             onBlur={() => keepText(l)}
             onKeyDown={(e) => {
               if (e.key === 'Enter') {
                 e.preventDefault();
                 keepText(l);
                 if (!isTicked) { setDraft({ at: i + 1 < openLines.length ? i + 1 : null, text: '' }); focusNext.current = 'draft'; }
               } else if (e.key === 'Backspace' && !lineText(l)) {
                 e.preventDefault();
                 const up = isTicked ? null : (openLines[i - 1] || null);
                 dropLine(l, up ? up.id : 'draft');
               }
             }} />
      <button type="button" className="tdn-remove" aria-label={`Delete line "${l.text}"`} title="Delete"
              disabled={busy} onClick={() => dropLine(l)}><Icon name="close" size={14} /></button>
    </li>
  );

  const draftLine = (
    <li key="draft" className="tdn-line is-new">
      <span className="tdn-grip is-gap" aria-hidden="true"><Icon name="plus" size={14} /></span>
      <input type="text" ref={draftRef} className="tdn-line-text" value={draft.text} maxLength={500}
             placeholder="List item" aria-label="New list item"
             onChange={(e) => setDraft({ ...draft, text: e.target.value })}
             onBlur={() => { if (draft.text.trim()) addDraft(); }}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { e.preventDefault(); addDraft(); }
               else if (e.key === 'Backspace' && !draft.text && draft.at !== null) {
                 e.preventDefault();
                 const up = openLines[draft.at - 1];
                 setDraft({ at: null, text: '' });
                 if (up) focusNext.current = up.id;
               }
             }} />
    </li>
  );

  const openRows = openLines.map((l, i) => line(l, i, false));
  if (draft.at === null) openRows.push(draftLine);
  else openRows.splice(draft.at, 0, draftLine);

  return (
    <div className="tds-wrap tdn-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className={`tds-dialog tdn-editor ${swatch(item.color)}${item.color ? ' has-color' : ''}${dropping ? ' is-dropping' : ''}`}
           role="dialog" aria-modal="true" aria-label={`Note ${item.title || 'without a title'}`} data-note-editor={item.id}
           onPaste={(e) => {
             const imgsIn = pickImages(e.clipboardData);
             if (!imgsIn.length) return;
             e.preventDefault();
             upload(imgsIn);
           }}
           onDragOver={(e) => {
             if (![...(e.dataTransfer?.types || [])].includes('Files')) return;
             e.preventDefault();
             setDropping(true);
           }}
           onDragLeave={(e) => { if (e.target === e.currentTarget) setDropping(false); }}
           onDrop={(e) => {
             const imgsIn = pickImages(e.dataTransfer);
             setDropping(false);
             if (!imgsIn.length) return;
             e.preventDefault();
             upload(imgsIn);
           }}>
        {imgs.length > 0 && (
          <div className={`tdn-ed-imgs n-${Math.min(3, imgs.length)}`} aria-label="Photos">
            {imgs.map((a) => (
              <span key={a.id} className="tdn-ed-img">
                <button type="button" className="tdn-img-open" aria-label={`Open ${a.file_name || 'photo'}`}
                        onClick={() => setViewing(a)}>
                  <NoteImage att={a} load={load} />
                </button>
                {/* A bin in the photo's corner, not a round ×: the editor's
                    only "close" is its Close button. */}
                {canRemove(a) && <button type="button" className="tdn-img-del" aria-label={`Remove ${a.file_name || 'photo'}`}
                        title="Remove photo" disabled={busy}
                        onClick={() => on.removeAttachment(item, a).catch((e) => fail(e, 'That could not be removed'))}>
                  <Icon name="trash" />
                </button>}
              </span>
            ))}
          </div>
        )}
        <div className="tdn-ed-head">
          <input type="text" className="tdn-ed-title" value={title} placeholder="Title" aria-label="Title"
                 maxLength={300} onChange={typed(setTitle)} autoFocus={!listMode && !item.title} />
          <button type="button" className={`tdn-tool${item.pinned ? ' is-on' : ''}`} disabled={busy}
                  aria-pressed={Boolean(item.pinned)} aria-label={item.pinned ? `Unpin ${name}` : `Pin ${name}`}
                  title={item.pinned ? 'Unpin' : 'Pin'}
                  onClick={() => on.pin(item).catch((e) => fail(e, 'That did not change'))}>
            <PinIcon on={item.pinned} />
          </button>
        </div>

        {!listMode ? (
          <textarea ref={bodyRef} className="tdn-ed-body" value={body} placeholder="Note" aria-label="Note" rows={3}
                    maxLength={20000} onChange={typed(setBody)} />
        ) : (
          <div className="tdn-ed-list">
            <ul className="tdn-lines" aria-label="Checklist" data-reorder="lines">{openRows}</ul>
            {ticked.length > 0 && (
              <div className="tdn-ticked">
                <button type="button" className="tdn-ticked-toggle" aria-expanded={showTicked}
                        onClick={() => setShowTicked((v) => !v)}>
                  <Icon name={showTicked ? 'chevronDown' : 'chevronRight'} size={14} />
                  {ticked.length === 1 ? '1 ticked item' : `${ticked.length} ticked items`}
                </button>
                {showTicked && (
                  <ul className="tdn-lines is-ticked" aria-label="Ticked items">
                    {ticked.map((l, i) => line(l, i, true))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {(voices.length > 0 || files.length > 0) && (
          <div className="tdn-ed-files">
            {voices.map((a) => (
              <NoteVoice key={a.id} att={a} load={load} download={download} onError={setErr}
                         onRemove={canRemove(a) ? () => on.removeAttachment(item, a).catch((e) => fail(e, 'That could not be removed')) : null} />
            ))}
            {files.map((a) => (
              <span key={a.id} className="tdn-file" data-attachment={a.id}>
                <button type="button" className="tdn-file-name" onClick={() => download(a).catch((e) => fail(e, 'Could not download it'))}>
                  {a.file_name}<span className="tdn-file-size">{fileSize(a.size_bytes)}</span>
                </button>
                {canRemove(a) && (
                  <button type="button" className="tdn-remove" aria-label={`Remove ${a.file_name}`}
                          onClick={() => on.removeAttachment(item, a).catch((e) => fail(e, 'That could not be removed'))}><Icon name="close" size={14} /></button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* What the note is in, and what is on it, as Keep shows it: chips.
            The list is a chip with a select inside; each label and reminder
            is its own chip (a press opens the picker below), and "+ Label"
            / "+ Reminder" ghost chips stand in while there are none. */}
        <div className="tdn-ed-meta">
          <label className="tdn-ed-chip is-select" title={fixedList ? 'Only the owner of this list can move its notes out of it' : 'List'}>
            <span className={`tda-dot ${!home || home.is_inbox ? 'sw-none' : swatch(home.color)}`} aria-hidden="true" />
            <select aria-label="List" value={item.list_id || ''} disabled={fixedList || busy}
                    onChange={(e) => on.moveList(item, e.target.value).catch((x) => fail(x, 'That did not move'))}>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <Icon name="chevronDown" size={12} />
          </label>
          {labels.length > 0 && (tags.length ? tags.map((b) => (
            <button key={b.id} type="button" className="tdn-ed-chip" data-meta="labels" aria-expanded={meta === 'labels'}
                    aria-label={`Label ${b.name} - change labels`} onClick={() => setMeta(meta === 'labels' ? null : 'labels')}>
              <span className={`tda-dot ${swatch(b.color)}`} aria-hidden="true" /><span className="tdn-chip-text">{b.name}</span>
            </button>
          )) : (
            <button type="button" className="tdn-ed-chip is-ghost" data-meta="labels" aria-expanded={meta === 'labels'}
                    aria-label="Add a label" onClick={() => setMeta(meta === 'labels' ? null : 'labels')}>
              <Icon name="plus" size={12} />Label
            </button>
          ))}
          {rems.length ? rems.map((r, i) => (
            <button key={r.id || `${i}-${reminderLabel(r)}`} type="button" className="tdn-ed-chip" data-meta="reminders"
                    aria-expanded={meta === 'reminders'} aria-label={`Reminder ${reminderLabel(r)} - change reminders`}
                    onClick={() => setMeta(meta === 'reminders' ? null : 'reminders')}>
              <Icon name="bell" size={12} /><span className="tdn-chip-text">{reminderLabel(r)}</span>
            </button>
          )) : (
            <button type="button" className="tdn-ed-chip is-ghost" data-meta="reminders" aria-expanded={meta === 'reminders'}
                    aria-label="Add a reminder" onClick={() => setMeta(meta === 'reminders' ? null : 'reminders')}>
              <Icon name="plus" size={12} />Reminder
            </button>
          )}
        </div>
        {meta === 'labels' && (
          <LabelPicker labels={labels} value={item.labels || []}
                       onChange={(next) => on.labels(item, next).catch((e) => fail(e, 'That did not change'))} />
        )}
        {meta === 'reminders' && (
          <div className="tdr">
            <ReminderPicker value={item.reminders || []} dueDate={item.due_date ? String(item.due_date).slice(0, 10) : null}
                            dueTime={item.due_time || null}
                            onChange={(next) => on.reminders(item, cleanReminders(next)).catch((e) => fail(e, 'That did not change'))} />
          </div>
        )}

        {recorder.recording && <VoiceBar recorder={recorder} />}
        {err && <p className="tdn-err" role="alert">{err}</p>}

        <div className="tdn-ed-foot">
          <div className="tdn-ed-tools">
            <Palette name={name} value={item.color} disabled={busy}
                     onPick={(c) => on.color(item, c).catch((e) => fail(e, 'That did not change'))} />
            <ImagePick label={`Add a photo to ${name}`} disabled={busy} onFiles={(f) => upload(f)} />
            {canRecord() && !recorder.recording && <MicButton recorder={recorder} disabled={busy} />}
            <button type="button" className="tdn-tool" disabled={busy} aria-pressed={listMode}
                    aria-label={listMode ? 'Hide checkboxes' : 'Show checkboxes'}
                    title={listMode ? 'Hide checkboxes' : 'Show checkboxes'} onClick={toggleChecklist}>
              <ChecklistIcon />
            </button>
            <button type="button" className="tdn-tool" disabled={busy}
                    aria-label={item.archived_at ? `Unarchive ${name}` : `Archive ${name}`}
                    title={item.archived_at ? 'Unarchive' : 'Archive'}
                    onClick={async () => { await saveRef.current(); on.archive(item, !item.archived_at); }}>
              <ArchiveIcon back={Boolean(item.archived_at)} />
            </button>
            <MoreMenu label={`More for note ${name}`} disabled={busy} items={[
              { label: 'Delete note', danger: true, onClick: () => on.remove(item) },
              { label: 'Make a copy', onClick: () => { saveRef.current().then(() => on.copy(item)); } },
              // Everything in the note goes with it (in place, on the server);
              // with no title it is named from what is in it.
              { label: 'Convert to to-do', disabled: !title.trim() && !body.trim() && !hasList && !hasFiles,
                onClick: () => { saveRef.current().then(() => on.toTask(item)); } }
            ]} />
          </div>
          <div className="tdn-ed-end">
            <span className="tdn-status" role="status" data-state={state}>{status || editedAt}</span>
            <button type="button" className="btn btn-secondary btn-small tdn-close" onClick={close}>Close</button>
          </div>
        </div>
      </div>
      {viewing && <ImageViewer att={viewing} load={load} onClose={() => setViewing(null)} />}
    </div>
  );
}
